import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { PRODUCTS, SERVICES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ value, size = "w-4 h-4", interactive = false, onChange, hover, setHover }) => (
  <div className="flex items-center gap-0.5" role={interactive ? "radiogroup" : undefined}>
    {[1, 2, 3, 4, 5].map((n) => {
      const filled = n <= (hover || value);
      const cls = `${size} ${filled ? "fill-[#D48C46] text-[#D48C46]" : "text-slate-600"} ${interactive ? "cursor-pointer transition-transform hover:scale-110" : ""}`;
      return interactive ? (
        <button key={n} type="button" aria-label={`${n} star${n > 1 ? "s" : ""}`} data-testid={`review-star-${n}`} onClick={() => onChange(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}>
          <Star className={cls} />
        </button>
      ) : (
        <Star key={n} className={cls} />
      );
    })}
  </div>
);

const ReviewCard = ({ r, idx, emerald }) => (
  <div className={`p-6 rounded-2xl border flex flex-col ${emerald ? "bg-[#06140E] border-emerald-900/60" : "bg-slate-900/60 border-slate-800"}`} data-testid={`review-card-${idx + 1}`}>
    <div className="flex items-center justify-between gap-3">
      <Stars value={r.rating} />
      <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md border ${emerald ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" : "text-rose-300 bg-rose-500/10 border-rose-500/30"}`}>{r.target}</span>
    </div>
    <p className="mt-4 text-sm text-slate-200 leading-relaxed flex-1">&ldquo;{r.text}&rdquo;</p>
    <div className="mt-5 flex items-center justify-between text-xs">
      <span className="font-bold text-white">{r.name}</span>
      <span className="text-slate-500">{new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
    </div>
  </div>
);

export const Reviews = ({ tone = "rose" }) => {
  const emerald = tone === "emerald";
  const [data, setData] = useState({ reviews: [], total: 0, average: 0 });
  const [form, setForm] = useState({ name: "", rating: 0, target: "Hair Oil", text: "" });
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const load = () => axios.get(`${API}/reviews`).then((res) => setData(res.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.rating || form.text.trim().length < 5) {
      toast.error("Please add your name, a star rating and a few words about your experience.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success("Thank you! Your review is now live.");
      setForm({ name: "", rating: 0, target: "Hair Oil", text: "" });
      await load();
    } catch {
      toast.error("Could not post your review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const accent = emerald ? "text-[#E5A862]" : "text-rose-400";
  const primary = emerald ? "bg-[#D48C46] hover:bg-[#E5A862] text-slate-950" : "bg-rose-600 hover:bg-rose-500 text-white";
  const focus = emerald ? "focus:border-[#D48C46]" : "focus:border-rose-500";

  return (
    <section id="reviews" className={`py-24 border-t ${emerald ? "bg-[#06140E] border-emerald-900/40" : "bg-slate-900/30 border-slate-800"}`} data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <span className={`text-xs font-semibold uppercase tracking-[0.25em] ${accent}`}>Customer Reviews</span>
            <h2 className="font-condensed text-4xl sm:text-5xl uppercase text-white mt-3 leading-tight">Real Experiences, Real Hair</h2>
            <div className="mt-6 flex items-center gap-4" data-testid="reviews-summary">
              <div className="font-condensed text-5xl text-white">{data.total ? data.average.toFixed(1) : "–"}</div>
              <div>
                <Stars value={Math.round(data.average)} size="w-5 h-5" />
                <div className="text-xs text-slate-400 mt-1" data-testid="reviews-count">{data.total ? `${data.total} review${data.total === 1 ? "" : "s"} from customers` : "Be the first to review"}</div>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="reviews-list">
              {data.reviews.length === 0 && (
                <div className={`md:col-span-2 p-8 rounded-2xl border border-dashed text-sm text-slate-400 ${emerald ? "border-emerald-900/60" : "border-slate-800"}`} data-testid="reviews-empty">
                  No reviews yet. Tried a Zoryven product or visited Fancy Saloon? Share your experience and help others choose.
                </div>
              )}
              {data.reviews.map((r, i) => (
                <ReviewCard key={r.id} r={r} idx={i} emerald={emerald} />
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <form onSubmit={handleSubmit} className={`p-8 rounded-3xl border lg:sticky lg:top-28 ${emerald ? "bg-[#0B1E16] border-emerald-900/60" : "bg-slate-900/80 border-slate-800"}`} data-testid="review-form">
              <h3 className="text-lg font-bold text-white">Share Your Experience</h3>
              <p className="text-xs text-slate-400 mt-1 mb-6">Tell others how a product or service worked for you.</p>

              <label className="block text-xs uppercase font-semibold text-slate-300 mb-2">Your Rating</label>
              <Stars value={form.rating} size="w-8 h-8" interactive onChange={(n) => setForm({ ...form, rating: n })} hover={hover} setHover={setHover} />

              <div className="mt-5">
                <label htmlFor="review-name" className="block text-xs uppercase font-semibold text-slate-300 mb-1">Your Name</label>
                <input id="review-name" name="name" value={form.name} onChange={handleChange} maxLength={60} placeholder="Name" data-testid="review-name-input" className={`w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none ${focus}`} />
              </div>
              <div className="mt-4">
                <label htmlFor="review-target" className="block text-xs uppercase font-semibold text-slate-300 mb-1">Product or Service</label>
                <select id="review-target" name="target" value={form.target} onChange={handleChange} data-testid="review-target-select" className={`w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-slate-300 text-sm focus:outline-none ${focus}`}>
                  <optgroup label="Zoryven Products">
                    {PRODUCTS.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
                  </optgroup>
                  <optgroup label="Fancy Saloon Services">
                    {SERVICES.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
                  </optgroup>
                </select>
              </div>
              <div className="mt-4">
                <label htmlFor="review-text" className="block text-xs uppercase font-semibold text-slate-300 mb-1">Your Experience</label>
                <textarea id="review-text" name="text" value={form.text} onChange={handleChange} rows={4} maxLength={600} placeholder="How did it work for you?" data-testid="review-text-input" className={`w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none resize-none ${focus}`} />
                <div className="text-[11px] text-slate-500 text-right mt-1">{form.text.length}/600</div>
              </div>
              <button type="submit" disabled={submitting} data-testid="review-submit-btn" className={`mt-4 w-full py-4 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-60 ${primary}`}>
                {submitting ? "Posting..." : "Post Review"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
